import PropTypes from "prop-types";
import { Card, Divider, Loader, Skeleton } from "@mantine/core";
import { HiCheck, HiXCircle, HiExclamationTriangle } from "react-icons/hi2";
import FetchFeedback from "../../ui/CustomFetchFeedback/CustomFetchFeedback";
import CustomEmptyState from "../../ui/CustomEmptyState/CustomEmptyState";
import {
  useGetBatchEvents,
  useGetManagementBatchEvents,
} from "../../../hooks/queries/useBulkUploadsQueries";
import { BULK_UPLOAD_STATUS_LABEL } from "../constants/bulkUploadsConstants";

const SKELETON_ROWS = 4;

// Batch events return DisbursementStatus as its display label (e.g. "Failed"),
// not the enum constant name, so terminal-state checks match against labels.
const TERMINAL_STATUSES = new Set([
  BULK_UPLOAD_STATUS_LABEL.COMPLETED,
  BULK_UPLOAD_STATUS_LABEL.PARTIALLY_COMPLETED,
  BULK_UPLOAD_STATUS_LABEL.PARTIALLY_PROCESSED,
  BULK_UPLOAD_STATUS_LABEL.FAILED,
]);

const EVENT_STATE = {
  COMPLETED: "completed",
  PARTIAL: "partial",
  FAILED: "failed",
  PROCESSING: "processing",
};

function getEventState(isLast, status) {
  if (!isLast) return EVENT_STATE.COMPLETED;
  if (!TERMINAL_STATUSES.has(status)) return EVENT_STATE.PROCESSING;
  if (status === BULK_UPLOAD_STATUS_LABEL.FAILED) return EVENT_STATE.FAILED;
  if (
    status === BULK_UPLOAD_STATUS_LABEL.PARTIALLY_COMPLETED ||
    status === BULK_UPLOAD_STATUS_LABEL.PARTIALLY_PROCESSED
  )
    return EVENT_STATE.PARTIAL;
  return EVENT_STATE.COMPLETED;
}

const LABEL_COLOR_CLASS = {
  [EVENT_STATE.COMPLETED]: "text-gray-900 font-semibold",
  [EVENT_STATE.PARTIAL]: "text-warning-700 font-semibold",
  [EVENT_STATE.FAILED]: "text-error-700 font-semibold",
  [EVENT_STATE.PROCESSING]: "text-gray-900 font-semibold",
};

const ICON_BASE_CLASS =
  "w-6 h-6 rounded-full flex items-center justify-center shrink-0";

function EventIcon({ state }) {
  const icons = {
    [EVENT_STATE.COMPLETED]: (
      <div className={`${ICON_BASE_CLASS} bg-success-600`}>
        <HiCheck size={13} className="text-white" />
      </div>
    ),
    [EVENT_STATE.PARTIAL]: (
      <div className={`${ICON_BASE_CLASS} bg-warning-500`}>
        <HiExclamationTriangle size={13} className="text-white" />
      </div>
    ),
    [EVENT_STATE.FAILED]: (
      <div className={`${ICON_BASE_CLASS} bg-error-600`}>
        <HiXCircle size={13} className="text-white" />
      </div>
    ),
    [EVENT_STATE.PROCESSING]: (
      <div className={`${ICON_BASE_CLASS} border-2 border-primary-500`}>
        <Loader size={11} color="primary" />
      </div>
    ),
  };

  return icons[state];
}

EventIcon.propTypes = {
  state: PropTypes.string.isRequired,
};

function SkeletonTimelineRow({ isLast }) {
  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center">
        <Skeleton height={24} width={24} radius="xl" />
        {!isLast && <div className="w-0.5 h-8 bg-gray-100 my-1.5" />}
      </div>
      <div className="pt-0.5 pb-6">
        <Skeleton height={14} width={160} radius="sm" mb={6} />
        <Skeleton height={12} width={110} radius="sm" />
      </div>
    </div>
  );
}

SkeletonTimelineRow.propTypes = {
  isLast: PropTypes.bool.isRequired,
};

function BatchTimelineSkeleton() {
  return (
    <Card radius="lg" className="p-5 sm:p-6 h-full">
      <Skeleton height={20} width={160} mb={16} radius="sm" />
      <Divider color="gray.2" mb={16} />
      {Array.from({ length: SKELETON_ROWS }, (_, index) => (
        <SkeletonTimelineRow key={index} isLast={index === SKELETON_ROWS - 1} />
      ))}
    </Card>
  );
}

export default function BatchProcessingTimeline({ id, isSuperAdmin = false }) {
  const merchantEventsQuery = useGetBatchEvents(id, { enabled: !isSuperAdmin });
  const managementEventsQuery = useGetManagementBatchEvents(id, {
    enabled: isSuperAdmin,
  });
  const {
    data: events = [],
    isLoading,
    isError,
    refetch,
  } = isSuperAdmin ? managementEventsQuery : merchantEventsQuery;

  if (isLoading) return <BatchTimelineSkeleton />;

  if (isError) {
    return (
      <Card
        radius="lg"
        className="p-5 sm:p-6 h-full flex items-center justify-center min-h-[300px]"
      >
        <FetchFeedback
          message="Failed to load processing timeline."
          onRetry={refetch}
          fullHeight={false}
          compact
        />
      </Card>
    );
  }

  if (events.length === 0) {
    return (
      <Card
        radius="lg"
        className="p-5 sm:p-6 h-full flex items-center justify-center min-h-[300px]"
      >
        <CustomEmptyState
          primary="No events recorded yet"
          secondary="Timeline will appear here once the batch is processed."
          height={200}
        />
      </Card>
    );
  }

  return (
    <Card radius="lg" className="p-5 sm:p-6 h-full">
      <p className="text-sm font-semibold text-gray-900 mb-3">
        Processing Timeline
      </p>
      <Divider color="gray.2" mb="md" />

      <div className="flex flex-col">
        {events.map((event, index) => {
          const isLast = index === events.length - 1;
          const eventState = getEventState(isLast, event.status);
          const labelColorClass = LABEL_COLOR_CLASS[eventState];

          const iconDelay = `${index * 0.35}s`;
          const contentDelay = `${index * 0.35 + 0.08}s`;
          const lineDelay = `${index * 0.35 + 0.18}s`;

          return (
            <div key={index} className="flex gap-3">
              <div className="flex flex-col items-center">
                <div
                  className="animate-[timeline-pop_0.28s_ease_both]"
                  style={{ animationDelay: iconDelay }}
                >
                  <EventIcon state={eventState} />
                </div>

                {!isLast && (
                  <div
                    className="w-0.5 flex-1 min-h-[28px] my-1.5 origin-top bg-success-500 animate-[timeline-grow_0.3s_ease_both]"
                    style={{ animationDelay: lineDelay }}
                  />
                )}
              </div>

              <div
                className="pb-5 pt-0.5 animate-[timeline-slide_0.28s_ease_both]"
                style={{ animationDelay: contentDelay }}
              >
                <p className={`text-sm leading-tight ${labelColorClass}`}>
                  {event.description}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {event.createdOn}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

BatchProcessingTimeline.propTypes = {
  id: PropTypes.string,
  isSuperAdmin: PropTypes.bool,
};
